import React, { useMemo } from 'react';
import { colors } from '../styles/colors';

const formatEuro = (v) => v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

/**
 * Composant d'affichage des cards de résumé par trimestre et annuel.
 * - Ligne principale : charges communes (tous les consultants)
 * - Ligne secondaire : charges spécifiques ventilées par consultant
 */
function ChargesSummaryCards({ nombreConsultants, charges, consultantsMap }) {
  const { shared, byConsultant } = useMemo(() => {
    const sh = { Q1: 0, Q2: 0, Q3: 0, Q4: 0 };
    // { consultantName: { Q1: x, Q2: x, Q3: x, Q4: x } }
    const bc = {};
    if (!charges) return { shared: sh, byConsultant: bc };

    const addToQuarter = (target, q, montant) => { target[q] += montant; };

    charges.forEach(charge => {
      const getQuarter = () => {
        if (charge.type === 'Trimestrielle' && charge.trimestre) return [charge.trimestre];
        if ((charge.type === 'Mensuelle' || charge.type === 'Exceptionnelle') && charge.mois) {
          return [charge.mois <= 3 ? 'Q1' : charge.mois <= 6 ? 'Q2' : charge.mois <= 9 ? 'Q3' : 'Q4'];
        }
        if (charge.type === 'Annuelle') return ['Q1', 'Q2', 'Q3', 'Q4'];
        return [];
      };

      const quarters = getQuarter();
      if (quarters.length === 0) return;

      if (!charge.consultant) {
        const montant = charge.type === 'Annuelle' ? charge.montant / 4 : charge.montant;
        quarters.forEach(q => addToQuarter(sh, q, montant));
      } else {
        const nom = (consultantsMap && consultantsMap[charge.consultant]) || charge.consultant;
        if (!bc[nom]) bc[nom] = { Q1: 0, Q2: 0, Q3: 0, Q4: 0 };
        const montant = charge.type === 'Annuelle' ? charge.montant / 4 : charge.montant;
        quarters.forEach(q => addToQuarter(bc[nom], q, montant));
      }
    });
    return { shared: sh, byConsultant: bc };
  }, [charges, consultantsMap]);

  const sharedAnnuel = shared.Q1 + shared.Q2 + shared.Q3 + shared.Q4;
  const consultantNames = Object.keys(byConsultant).sort();
  const hasSpecific = consultantNames.length > 0;

  const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];

  return (
    <div style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {/* Ligne principale : charges communes */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem' }}>
        {quarters.map(q => {
          const quotePart = nombreConsultants > 0 ? shared[q] / nombreConsultants : 0;
          return (
            <div key={q} style={{
              padding: '1rem',
              backgroundColor: colors.primary.teal,
              borderRadius: '0.5rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '0.875rem', color: colors.text.white, marginBottom: '0.5rem', opacity: 0.9 }}>
                {q}
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: colors.text.white }}>
                {formatEuro(shared[q])}
              </div>
              {nombreConsultants > 0 && (
                <div style={{
                  fontSize: '0.75rem', color: colors.text.white, marginTop: '0.5rem',
                  opacity: 0.8, borderTop: '1px solid rgba(255,255,255,0.3)', paddingTop: '0.5rem'
                }}>
                  Quote-part: {formatEuro(quotePart)}
                </div>
              )}
            </div>
          );
        })}
        <div style={{
          padding: '1rem',
          backgroundColor: colors.primary.gray,
          borderRadius: '0.5rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '0.875rem', color: 'white', marginBottom: '0.5rem' }}>Total Annuel</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'white' }}>
            {formatEuro(sharedAnnuel)}
          </div>
          {nombreConsultants > 0 && (
            <div style={{
              fontSize: '0.75rem', color: 'white', marginTop: '0.5rem',
              opacity: 0.8, borderTop: '1px solid rgba(255,255,255,0.3)', paddingTop: '0.5rem'
            }}>
              Quote-part: {formatEuro(sharedAnnuel / nombreConsultants)}
            </div>
          )}
        </div>
      </div>

      {/* Ligne secondaire : charges par consultant */}
      {hasSpecific && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem' }}>
          {quarters.map(q => {
            const entries = consultantNames.filter(n => byConsultant[n][q] !== 0);
            if (entries.length === 0) return <div key={q} />;
            return (
              <div key={q} style={{
                padding: '0.5rem 0.75rem',
                backgroundColor: colors.secondary.lightTeal,
                borderRadius: '0.375rem',
                textAlign: 'left'
              }}>
                <div style={{ fontSize: '0.85rem', color: colors.primary.gray, opacity: 0.9, marginBottom: '0.25rem', textAlign: 'center' }}>
                  Par consultant
                </div>
                {entries.map(nom => (
                  <div key={nom} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    fontSize: '0.85rem', color: colors.primary.gray, lineHeight: 1.6
                  }}>
                    <span>{nom}</span>
                    <span style={{ fontWeight: '700' }}>{formatEuro(byConsultant[nom][q])}</span>
                  </div>
                ))}
              </div>
            );
          })}
          <div style={{
            padding: '0.5rem 0.75rem',
            backgroundColor: colors.primary.gold || '#b8860b',
            borderRadius: '0.375rem',
            textAlign: 'left'
          }}>
            <div style={{ fontSize: '0.85rem', color: 'white', opacity: 0.9, marginBottom: '0.25rem', textAlign: 'center' }}>
              Par consultant
            </div>
            {consultantNames.map(nom => {
              const annuel = byConsultant[nom].Q1 + byConsultant[nom].Q2 + byConsultant[nom].Q3 + byConsultant[nom].Q4;
              return (
                <div key={nom} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  fontSize: '0.85rem', color: 'white', lineHeight: 1.6
                }}>
                  <span>{nom}</span>
                  <span style={{ fontWeight: '700' }}>{formatEuro(annuel)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default ChargesSummaryCards;
